import { Box, Flex, Link, ListItem, VStack } from '@chakra-ui/react'
import React from 'react'
import { dimensions, spacing } from '~/constants/theme'
import { themeColorSectionKeys } from '~/themes/colors/colors'

const {
  backgroundColorDefault,
  backgroundColorAlert,
  borderColorAlert,
  borderColorDefault,
  subheadingSectionBackgroundColor,
  helpListItemColor,
  helpLinkColor,
  helpLinkHoverColor,
  menuItemWrapperBackgroundColor,
} = themeColorSectionKeys.Settings.Shared

export const DefaultSettingsWrapper = ({ children }) => {
  return (
    <Flex
      width={'full'}
      height={'full'}
      padding={'1.875rem'}
      flexDir={'column'}
      gap={'1.25rem'}
      overflowY={'auto'}
    >
      {children}
    </Flex>
  )
}

export const SubheadingSection = ({ children }) => {
  return (
    <Flex
      width={'100%'}
      borderRadius={'0.25rem'}
      display={'flex'}
      flexDir={'column'}
      alignItems={'flex-start'}
      padding={'0.75rem'}
      gap={'0.62rem'}
      backgroundColor={subheadingSectionBackgroundColor}
    >
      {children}
    </Flex>
  )
}

export const MenuItemWrapper = ({ isAlert, children, ...props }) => (
  <Box
    width="100%"
    height={isAlert ? '59px' : 'aut'}
    padding={`${spacing.spacingLG} ${spacing.spacingMD}`}
    backgroundColor={
      isAlert ? backgroundColorAlert : menuItemWrapperBackgroundColor
    }
    border="1.5px solid"
    borderColor={isAlert ? borderColorAlert : borderColorDefault}
    borderRadius="0.25rem"
    display="flex"
    flexDirection="column"
    boxSizing="border-box"
    cursor="pointer"
    _hover={{
      backgroundColor: borderColorDefault,
    }}
    _active={{
      backgroundColor: borderColorDefault,
      borderColor: borderColorDefault,
    }}
    {...props}
  >
    {children}
  </Box>
)

export const MenuItemPrimaryContentWrapper = ({
  hasAlert,
  children,
  ...props
}) => (
  <Flex
    width="100%"
    flexDirection="row"
    alignItems="flex-start"
    position="relative"
    justifyContent={'space-between'}
    marginBottom={hasAlert ? spacing.spacingSM : 0}
    {...props}
  >
    {children}
  </Flex>
)

export const MenuItemAlertContentWrapper = ({ children, ...props }) => (
  <Flex
    padding="0px 34px"
    alignItems="flex-end"
    gap={spacing.spacingLG}
    alignSelf="stretch"
    marginBottom={spacing.spacingSM}
    {...props}
  >
    {children}
  </Flex>
)

export const MenuItemHeader = ({ roundBottomCorners, children, ...props }) => (
  <Flex
    width="100%"
    padding={props.padding ?? `${spacing.spacingLG} ${spacing.spacingXL}`}
    border={`1.5px solid ${borderColorDefault}`}
    borderRadius={`${dimensions.borderRadiusXS} ${dimensions.borderRadiusXS} ${roundBottomCorners ? dimensions.borderRadiusXS : '0'} ${roundBottomCorners ? dimensions.borderRadiusXS : '0'}`}
    alignItems="center"
    position="relative"
    cursor="pointer"
    justifyContent={'space-between'}
    backgroundColor={backgroundColorDefault}
    _hover={{
      backgroundColor: borderColorDefault,
    }}
    {...props}
  >
    {children}
  </Flex>
)

export const MenuItemPanel = ({ children, ...props }) => (
  <VStack
    width="100%"
    height="fit-content"
    padding={`${spacing.spacingXXXL} ${spacing.spacingLG} ${spacing.spacingLG}`}
    border={`1.5px solid ${borderColorDefault}`}
    borderRadius={`0 0 ${dimensions.borderRadiusXS} ${dimensions.borderRadiusXS}`}
    alignItems="center"
    position="relative"
    cursor="pointer"
    spacing={spacing.spacingLG}
    {...props}
  >
    {children}
  </VStack>
)

export const DependentSectionPanel = ({ children, ...props }) => (
  <VStack
    width="100%"
    height="fit-content"
    alignItems="center"
    spacing={spacing.spacingLG}
    position="relative"
    cursor="default"
    {...props}
  >
    {children}
  </VStack>
)

export const ToggleWrapper = ({ isInHeader, children, ...props }) => (
  <Box
    display="flex"
    right={isInHeader ? spacing.spacingXL : spacing.spacingMD}
    {...props}
  >
    {children}
  </Box>
)

interface DropdownWrapperProps {
  bottomMargin?: string
  children: React.ReactNode
}

export const DropdownWrapper: React.FC<DropdownWrapperProps> = ({
  children,
  bottomMargin,
}) => {
  return (
    <Flex
      width={'100%'}
      marginBottom={bottomMargin ?? '0'}
      flexDir={'column'}
      gap={'0.5rem'}
    >
      {children}
    </Flex>
  )
}

export const HelpListItem = ({ children }) => {
  return (
    <ListItem marginLeft={'-2rem'} color={helpListItemColor}>
      {children}
    </ListItem>
  )
}

export const HelpLink = ({ href, target, rel, children }) => {
  return (
    <Link
      href={href}
      target={target}
      rel={rel}
      color={helpLinkColor}
      _hover={{
        color: helpLinkHoverColor,
        textDecoration: 'underline',
      }}
    >
      {children}
    </Link>
  )
}
